import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plug } from "lucide-react";
import {
  Button,
  DataTable,
  FeedbackState,
  localTablePage,
  Mono,
  PageContent,
  PageHeader,
  PageShell,
  TableIdentity,
  type DataTableColumn,
  type DataTableState,
} from "@h2j-data-team/ui";
import type { QlikConnection } from "@repo/shared";

import { supportCode } from "@/components/error-screen";
import { DensityToggle } from "@/components/density-toggle";
import { useTableDensity } from "@/lib/use-table-density";
import { useSearchHotkey } from "@/lib/use-search-hotkey";
import { listRefreshing, paginationLabels, tableMessages, tableSearch } from "@/lib/ui-labels";
import { connectionsQueryOptions } from "@/features/qlik-connections/api";

export const Route = createFileRoute("/_authenticated/capacity/")({
  validateSearch: (search: Record<string, unknown>) => ({
    q: typeof search.q === "string" ? search.q : undefined,
  }),
  component: CapacityConnectionsPage,
});

function CapacityConnectionsPage() {
  const { t } = useTranslation("capacity");
  const { t: tc } = useTranslation("common");
  const { density, setDensity } = useTableDensity();
  const { q } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const searchRef = useRef<HTMLInputElement>(null);
  useSearchHotkey(searchRef);
  const connectionsQuery = useQuery(connectionsQueryOptions);
  const { data: connections } = connectionsQuery;

  // The search lives in the URL; the page and its size here.
  const [paging, setPaging] = useState({ page: 1, pageSize: 50 });
  const state: DataTableState = {
    ...paging,
    query: q ?? "",
    filters: {},
    selection: [],
  };

  const columns = useMemo<DataTableColumn<QlikConnection>[]>(
    () => [
      {
        id: "name",
        header: t("index.table.name"),
        rowHeader: true,
        rowLink: true,
        variant: "identity",
        value: (connection) => connection.name,
        cell: (connection) => (
          <TableIdentity
            leading={<Plug />}
            name={
              <Link
                to="/capacity/$connectionId"
                params={{ connectionId: connection.id }}
                search={{ tab: "byApp" }}
                className="rounded-sm hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                {connection.name}
              </Link>
            }
          />
        ),
      },
      {
        id: "url",
        header: t("index.table.url"),
        cell: (connection) => (
          <span className="font-mono text-dim">
            {connection.baseUrl.replace(/^https?:\/\//, "").replace(/\/$/, "")}
          </span>
        ),
      },
    ],
    [t],
  );

  const page = localTablePage(connections ?? [], state, { columns });

  const clearSearch = () => navigate({ search: () => ({ q: undefined }), replace: true });

  const code = connectionsQuery.isError
    ? supportCode(connectionsQuery.error, tc("error.supportCode"))
    : undefined;

  return (
    <PageShell header={<PageHeader title={t("title")} description={t("description")} />}>
      <PageContent fill>
        {connections && connections.length === 0 ? (
          <FeedbackState
            state="empty"
            icon={<Plug />}
            title={t("empty.noConnections.title")}
            description={t("empty.noConnections.description")}
            action={
              <Button asChild>
                <Link to="/connections/new">{t("empty.noConnections.action")}</Link>
              </Button>
            }
          />
        ) : (
          <DataTable
            label={t("title")}
            density={density}
            columns={columns}
            rows={page.rows}
            getRowId={(connection) => connection.id}
            state={state}
            onStateChange={(next) => {
              setPaging({ page: next.page, pageSize: next.pageSize });
              if (next.query !== state.query) {
                navigate({
                  search: (prev) => ({ ...prev, q: next.query || undefined }),
                  replace: true,
                });
              }
            }}
            loading={connectionsQuery.isPending}
            refreshing={listRefreshing(connectionsQuery)}
            error={
              connectionsQuery.isError
                ? {
                    title: t("index.failedToLoad"),
                    description: code && (
                      <>
                        {code.label}: <Mono>{code.value}</Mono>
                      </>
                    ),
                  }
                : null
            }
            onRetry={() => void connectionsQuery.refetch()}
            search={{
              ...tableSearch(t("index.searchPlaceholder")),
              inputRef: searchRef,
              shortcutHint: "/",
            }}
            toolbar={<DensityToggle value={density} onChange={setDensity} />}
            pagination={{ kind: "offset", total: page.total, labels: paginationLabels() }}
            messages={tableMessages({
              empty: { icon: <Plug />, title: t("empty.noConnections.title") },
              noResults: {
                title: t("index.noMatch.title"),
                description: t("index.noMatch.description", { query: q }),
                action: (
                  <Button variant="outline" size="sm" onClick={clearSearch}>
                    {t("index.noMatch.clearSearch")}
                  </Button>
                ),
              },
            })}
          />
        )}
      </PageContent>
    </PageShell>
  );
}
